(ns practitest.frontend.components.selector
  (:require [clojure.string :as string]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.components.overflow :as overflow]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.common.i18n :refer [get-translation format-translation]]
            [practitest.frontend.components.overlay2 :as overlay2]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.components2.misc-components :as misc-components]
            [practitest.frontend.components2.search-box :as search-box]
            [reagent.core :as reagent]
            [practitest.frontend.common.format-utils :as fu]
            ["./scrollutils" :as scrollutils]
            [clojure.string :as str]))

(defn- do-tags-update
  [{:keys [selector-state on-change value special-input-ref]} item]
  ;; For tags it's append on click, stay opened, clear filter and focus on filter input
  (swap! selector-state assoc
         :filter-value ""
         :keyboard-selected-item nil)
  (on-change (conj value item))
  (dom/focus-on-ref! special-input-ref true))

(defn default-selector-item
  [{:keys [selected?
           hover?
           on-change
           label-fn
           item
           value
           multi?
           tags?
           selector-state
           inner-ref
           on-hover]
    :as props}]
  [:div.selector-item
   {:class         (dom/classes-pairs :active selected?
                                      :hover hover?)
    :ref           inner-ref
    :on-mouse-over on-hover
    :on-mouse-move on-hover
    :on-click      (fn [e]
                     (cond
                       tags?
                       (do-tags-update props item)

                       ;; For multilist it's intermediate state before Cancel or Apply is clicked
                       multi?
                       (if selected?
                         (swap! selector-state update :selected-values disj item)
                         (swap! selector-state update :selected-values conj item))

                       :else
                       (when on-change
                         (on-change item))))}

   (when (and (not tags?)
              multi?)
     [:input.m-checkbox
      {:type      "checkbox"
       :tab-index -1
       :read-only true
       :checked   selected?}])

   [overflow/overflow-with-mux-tooltip
    {}
    (fn [props]
      [:span.selector-item-text
       (assoc props
         :class (dom/classes-pairs :active selected?))
       (label-fn item)])
    (label-fn item)]

   (when (and selected?
              (not multi?))
     [:span.icon-holder
      [icons/mux-checkbox]])])

(defn default-selected-item
  [{:keys [show? value multi? label-fn]}]
  [:div.selector-current-item
   {:class (dom/classes-pairs :expanded show?)}

   (when value
     (let [formatted-value (if multi?
                             (str/join ", " (map label-fn value))
                             (label-fn value))]
       [overflow/overflow-with-mux-tooltip
        {}
        (fn [props]
          [:span.selector-item-text
           props
           formatted-value])
        formatted-value]))])

(defn tag-pill-element
  [{:keys [on-change value label-fn non-removable-items]} item]
  (let [removable? (not (contains? (or non-removable-items #{}) item))]
    [:div.m-tag-pill
     [overflow/overflow-with-mux-tooltip
      {}
      (fn [tooltip-props]
        [:span.m-tag-label tooltip-props (label-fn item)])
      (label-fn item)]

     [:div.m-tag-control-container
      (when removable?
        [:div.delete-tag-value
         {:on-click (fn [e]
                      (dom/stop-propagation e)
                      (when on-change
                        (on-change (disj value item))))}
         [icons/mux-x-small]])]]))

(defn tags-selected-items
  [{:keys [show? hide! value selector-state special-input-ref] :as props}]
  [:div.selector-current-item.tags-current-item
   {:class (dom/classes-pairs :expanded show?)}
   (doall
    (for [tag value]
      (with-meta
        [tag-pill-element props tag]
        {:key (:id tag)})))

   (when show?
     [:input.special-input
      {:ref (fn [node]
              (reset! special-input-ref node)
              ;; autofocus on mount
              (dom/focus-on-ref! special-input-ref true))
       :value (:filter-value @selector-state)
       :on-click (fn [e]
                   ;; Prevent dropdown from closing when clicking on input to get focus
                   (dom/stop-propagation e))
       :on-blur (fn [e]
                  ;; relatedTarget would be undefined if we're clicking inside the dropdown (selecting new tag)
                  ;; but will be populated when we're pressing Tab to move to other component
                  ;; On blur handler is disabled now because it causes a lot of strange issues
                  #_(when (.-relatedTarget e)
                    (hide!)))

       :on-change (fn [e]
                    (swap! selector-state assoc :filter-value (dom/event-value e)
                           :keyboard-selected-item nil))}])])

(defn- non-nil-wins
  [a b]
  (if (nil? b)
    a
    b))

(defn- next-item
  [selected-item items]
  (if (nil? selected-item)
    (first items)
    (let [tail (next (drop-while #(not= % selected-item) items))]
      (if (seq tail)
        (first tail)
        (first items)))))

(defn- prev-item
  [selected-item items]
  (if (nil? selected-item)
    (last items)
    (let [head (take-while #(not= % selected-item) items)]
      (if (seq head)
        (last head)
        (last items)))))

(defn- scroll-into-view
  [menu-ref element-ref]
  (when (and menu-ref element-ref
             @menu-ref @element-ref)
    (scrollutils/scrollIntoView @menu-ref @element-ref)))

;; New select
;; TODO: refactor to make project selector an instance of this one
(defn new-select [props]
  (let [selector-state (reagent/atom {:show?                  (:autofocus? props)
                                      :keyboard-selected-item (when (and (not (:tags? props))
                                                                         (not (:multi? props)))
                                                                (:value props))
                                      :filter-value           nil
                                      ;; For multilist mode
                                      :selected-values        (if-some [value (:value props)]
                                                                (set value)
                                                                #{})})

        menu-list-ref (atom nil)
        focused-item-ref (atom nil)
        autofocused (atom false)
        selector-input-ref (atom nil)
        special-input-ref (atom nil)
        filter-input-ref (atom nil)
        overlay-placement (reagent/atom nil)

        prepare-default-props (fn [props]
                                (->> props
                                     ;; Nilable default props
                                     (merge
                                       {:selector-state selector-state
                                        :show?          (:show? @selector-state)
                                        :show!          (fn [e]
                                                          (swap! selector-state assoc
                                                                 :show? true
                                                                 :selected-values (if-some [value (:value props)]
                                                                                    (set value)
                                                                                    #{}))
                                                          (when-let [on-focus (:on-focus props)]
                                                            (on-focus e))
                                                          (dom/focus-on-ref! filter-input-ref false))
                                        :hide! (fn [e]
                                                 (swap! selector-state assoc :show? false
                                                        :keyboard-selected-item nil
                                                        :filter-value nil)
                                                 (when-let [on-blur (:on-blur props)]
                                                   (on-blur e)))
                                        :multi? false})
                                     ;; Non-nilable default props
                                     (merge-with
                                       non-nil-wins
                                       {:key-fn :id
                                        :label-fn :label
                                        :placement "bottom"
                                        :special-input-ref special-input-ref
                                        :selector-item-renderer default-selector-item
                                        :selected-item-renderer default-selected-item})))]
    (reagent/create-class
      {:component-did-update (fn [this]
                               ;; When element is focused we need to scroll to it
                               ;; to support correct behavior in keyboard mode
                               ;(scroll-into-view menu-list-ref focused-item-ref)

                               ;; Disabled for now, because it's causing extra scrolling when cursor is near
                               ;; border
                               )

       :reagent-render
       (fn [props]
         (let [{:keys [show?
                       multi?

                       ;; TODO: replace with proper tag cloud
                       tags?
                       field-type

                       show!
                       hide!
                       placement

                       value
                       items

                       key-fn
                       label-fn

                       id
                       test-id
                       data-level
                       aria-labelledby
                       class

                       placeholder
                       clearable?

                       selector-item-renderer
                       selected-item-renderer

                       autofocus?

                       on-change
                       on-add-value
                       on-refresh-values
                       is-valid-new-tag-fn
                       no-create-permission-message

                       close-on-mouse-leave?

                       filterable?
                       empty-placeholder

                       strategy
                       trace?]
                :as   props} (prepare-default-props props)
               labels? (= field-type :labels)
               show-filter? (and (not tags?) (not (false? filterable?)))

               filter-value (:filter-value @selector-state)
               has-filter? (not (str/blank? filter-value))

               has-value? (if multi?
                            (seq value)
                            (some? value))

               all-items items

               ;; special preprocessing for tags - remove already assigned tags from the list
               items (if tags?
                       (remove #(contains? value %) items)
                       items)

               filtered-items (filter #(if has-filter?
                                         (fu/substring-search? (label-fn %) filter-value)
                                         true)
                                      items)

               new-tag-valid? (if is-valid-new-tag-fn
                                (is-valid-new-tag-fn filter-value)
                                true)

               new-tag? (and tags?
                             on-add-value
                             (not (str/blank? filter-value))
                             (empty? (filter #(= (label-fn %) filter-value) filtered-items)))

               selected-values (:selected-values @selector-state)

               ;; Handles arrows / enter / escape — safe to attach to both the wrapper
               ;; and the filter input, since none of these are characters the user types.
               nav-key-handler-fn (fn [e]
                                    (let [key (.-key e)]
                                      (cond
                                        (= "ArrowDown" key)
                                        (do
                                          (dom/prevent-default e)
                                          (when (seq filtered-items)
                                            (swap! selector-state
                                                   update :keyboard-selected-item
                                                   next-item filtered-items)))

                                        (= "ArrowUp" key)
                                        (do
                                          (dom/prevent-default e)
                                          (when (seq filtered-items)
                                            (swap! selector-state
                                                   update :keyboard-selected-item
                                                   prev-item filtered-items)))

                                        (= "Enter" key)
                                        (do
                                          (dom/prevent-default e)
                                          (dom/stop-propagation e)
                                          (if show?
                                            (cond
                                              (and tags? (not new-tag?))
                                              (do-tags-update props (:keyboard-selected-item @selector-state))

                                              (not multi?)
                                              (do
                                                (on-change (:keyboard-selected-item @selector-state))
                                                ;; Move focus before hiding when input filter is present
                                                ;; otherwise tab-cycle and blur/focus is broken
                                                (when (and show-filter? selector-input-ref @selector-input-ref)
                                                  (.focus @selector-input-ref))
                                                (hide!))

                                              ;; Do nothing in case of multilist (or in case of new tag -
                                              ;; we decided not to add tags on enter click)
                                              :else
                                              nil)

                                            (show!)))

                                        (= "Escape" key)
                                        (do
                                          (dom/prevent-default e)
                                          (dom/stop-propagation e)
                                          (when show?
                                            (hide!))))))

               ;; Wrapper-only handler: navigation plus space-toggles-selection.
               ;; Must NOT be attached to the filter input — space there is a typed character.
               key-handler-fn (fn [e]
                                (if (and (= " " (.-key e))
                                         (not tags?)
                                         multi?)
                                  (do
                                    (dom/prevent-default e)
                                    (when-let [selected-item (get @selector-state :keyboard-selected-item)]
                                      (when-not (nil? selected-item)
                                        (swap! selector-state update :selected-values
                                               (fn [selected-values]
                                                 (if (contains? selected-values selected-item)
                                                   (disj selected-values selected-item)
                                                   (conj selected-values selected-item)))))))
                                  (nav-key-handler-fn e)))
               ;; Actual placement can be different from requested due to e.g. flip middleware
               actual-placement @overlay-placement
               placement-class (if (some? actual-placement)
                                 (str actual-placement)
                                 ;; use requested placement by default
                                 (str placement))]
           [overlay2/floating-ui-overlay
            (cond-> {:placement        placement
                     :show?            show?
                     :flip             true
                     :max-size?        true
                     :on-placement-change #(reset! overlay-placement %)
                     :on-request-close (fn []
                                         (hide! nil))
                     ;; Match input width
                     :target-width?    true
                     :trace?           trace?
                     :strategy         strategy}
              close-on-mouse-leave? (assoc :flip-padding 5))

            ;; Target
            [:div.selector-input
             (dom/add-test-id
               {:class       (dom/append-class (dom/classes-pairs-v :focused show?
                                                                    :expanded show?
                                                                    placement-class true)
                                               class)
                :id          id
                :data-level  data-level
                :aria-labelledby aria-labelledby
                :tab-index   0
                :on-key-down key-handler-fn
                :ref         (fn [node]
                               (reset! selector-input-ref node)

                               (when (and node autofocus?
                                          ;; Only once
                                          (not @autofocused))
                                 (reset! autofocused true)
                                 (show!)
                                 ;; In case of filter present we want to auto-focus on input
                                 (when-not show-filter?
                                   (.focus node))))
                :on-click    (fn [e]
                               (if show?
                                 (hide! e)
                                 (show! e)))}
               test-id)

             ;; Overridable selected item
             (if tags?
               [tags-selected-items props]
               [selected-item-renderer props])

             (when (and (not tags?)
                        (not has-value?)
                        placeholder)
               [:span.selector-placeholder placeholder])

             [:span.selector-controls-holder
              (when (and has-value? clearable?)
                [:<>
                 [:span.selector-clear-value
                  {:data-testid "searchable-tree-clear-value"
                   :on-click (fn [e]
                               (dom/stop-propagation e)
                               (swap! selector-state assoc :selected-values #{})
                               (when on-change
                                 (on-change nil)))}
                  [icons/mux-x]]

                 [:div.selector-controls-delimiter]])

              [:span.icon-holder
               (if show?
                 [icons/mux-chevron-up2]
                 [icons/mux-chevron-down2])]]]

            ;; Dropdown body
            [:div.selector-dropdown.expanded
             {:class placement-class
              :on-mouse-leave (when close-on-mouse-leave?
                                (fn [e]
                                  (hide! e)))}
             (when show-filter?
               [search-box/search-textbox
                {:tab-index 0
                 :ref filter-input-ref
                 :placeholder (get-translation "generic.dropdown.search-placeholder" "Search")
                 :on-key-down nav-key-handler-fn
                 :value filter-value
                 :on-change (fn [e]
                              (swap! selector-state assoc :filter-value (dom/event-value e)))
                 :on-clear-click (fn [e]
                                   (swap! selector-state assoc :filter-value ""))}])

             ;; Multilist support
             (when (and (not tags?)
                        multi?)
               (let [selected? (seq (:selected-values @selector-state))
                     all-selected? (= (:selected-values @selector-state)
                                      (set filtered-items))]
                 [:<>
                  [:div.selector-multilist-controls
                   [misc-components/link-button
                    {:disabled? all-selected?
                     :on-click (fn [e]
                                 (swap! selector-state assoc :selected-values (set filtered-items)))}
                    (get-translation "generic.button.select-all" "Select All")]
                   [misc-components/link-button
                    {:class "pull-right"
                     :disabled? (not selected?)
                     :on-click (fn [e]
                                 ;; TODO: filtered only ?
                                 (swap! selector-state assoc :selected-values #{}))}
                    (get-translation "generic.button.clear" "Clear")]]
                  [:hr.selector-multilist-divider
                   {:style {:margin-bottom "0.38rem"}}]]))

             [:div.selector-items-container
              {:ref #(reset! menu-list-ref %)}
              (when show?
                (if (and tags?
                         (not has-filter?)
                         (empty? filtered-items))
                  ;; Special case for empty tags/labels list (either completely empty or empty filtered list)
                  (let [no-items-key (if labels? "generic.list.no-labels-yet-placeholder" "generic.list.no-tags-yet-placeholder")
                        no-items-default (if labels? "No Labels yet, Start typing to add" "No Tags yet, Start typing to add")
                        filtered-key (if labels? "generic.list.filtered-labels-empty-placeholder" "generic.list.filtered-tags-empty-placeholder")
                        filtered-default (if labels? "Start typing to add new label" "Start typing to add new tag")]
                    [:div.selector-item-placeholder
                     [:span.selector-placeholder
                      (if (empty? all-items)
                        (get-translation no-items-key no-items-default)
                        (get-translation filtered-key filtered-default))]])

                  ;; Usual case - show filtered items
                  (if (empty? filtered-items)
                    [:div.selector-item-placeholder
                     [:span.selector-placeholder
                      (or empty-placeholder
                          (get-translation "generic.dropdown.no-results" "No results found"))]]
                    (->> filtered-items
                         (map-indexed
                           (fn [idx item]
                             ;; Provide index to key-fn as well, useful when id is not unique (e.g. multilists and lists currently)
                             ^{:key (key-fn item idx)}
                             [selector-item-renderer
                              (assoc props
                                     :on-hover (fn [e]
                                                 (swap! selector-state assoc :keyboard-selected-item item))
                                     :on-change (fn [item]
                                                  ;; Hide menu after value is selected (not for tags)
                                                  (when-not tags?
                                                    (hide!))
                                                  (when on-change
                                                    (on-change item)))
                                     :inner-ref (when (= (:keyboard-selected-item @selector-state) item)
                                                  (fn [node]
                                                    (reset! focused-item-ref node)))
                                     :selected? (when-not tags?
                                                  (if multi?
                                                    (contains? selected-values item)
                                                    (= value item)))
                                     :hover? (= (:keyboard-selected-item @selector-state) item)
                                     :item item)]))
                         (doall)))))]

             (when (and has-filter?
                        no-create-permission-message)
               [:div.selector-no-create-permission-message
                no-create-permission-message])

             (cond
               ;; Special case - new tag is entered in filter input box
               new-tag?
               [misc-components/link-button
                {:disabled? (not new-tag-valid?)
                 :on-click (fn [e]
                             (when new-tag-valid?
                               (on-add-value filter-value)
                               (do-tags-update props {:label filter-value
                                                      :id filter-value})))}
                [:div.selector-add-new-value
                 [icons/mux-plus-circle]
                 (format-translation
                  (if labels?
                    (get-translation "generic.list.add-new-label" (str "Add \"%{val}\" as new label"))
                    (get-translation "generic.list.add-new-tag" (str "Add \"%{val}\" as new tag")))
                  :val filter-value)]]

               (and (not tags?)
                    on-refresh-values)
               [misc-components/link-button
                {:on-click (fn [e] (on-refresh-values))}
                [:div.selector-add-new-value
                 [icons/mux-refresh]
                 (get-translation "generic.list.refresh-jira-values" "Refresh Values (Jira)")]]

               (and (not tags?)
                    on-add-value)
               [misc-components/link-button
                {:on-click (fn [e]
                             ;; Value will be prompted from user in this case
                             (on-add-value nil))}
                [:div.selector-add-new-value
                 [icons/mux-plus-circle]
                 (get-translation "generic.list.add-new-value" "Add new value")]])

             (when (and (not tags?)
                        multi?)
               [:<>
                [:hr.selector-multilist-divider]
                [:div.selector-multi-buttons
                 [buttons/button
                  {:on-click (fn [e]
                               (when (and show-filter? selector-input-ref @selector-input-ref)
                                 (.focus @selector-input-ref))
                               (hide! e))
                   :text (get-translation "generic.button.cancel" "Cancel")}]
                 [buttons/button
                  {:primary? true
                   :on-click (fn [e]
                               (when (and show-filter? selector-input-ref @selector-input-ref)
                                 (.focus @selector-input-ref))
                               (hide! e)
                               (when on-change
                                 (on-change selected-values)))
                   :text (get-translation "generic.button.apply" "Apply")}]]])]]))})))
